import { useState, useCallback, useRef, useEffect } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

const THEMES = [
  { id: "ecstasy", name: "Spirit of Ecstasy", style: "polished chrome and silver metal sculpture on a sleek circular chrome pedestal with mirror-like reflective surface, like a Rolls Royce hood ornament" },
  { id: "bronze", name: "Flowing Bronze", style: "warm polished bronze sculpture with subtle patina on edges, smooth organic Henry Moore-inspired curves, on a dark walnut circular base" },
  { id: "jade", name: "Jade Dynasty", style: "translucent emerald jade sculpture with deep veining and luminous inner glow, polished mirror finish, on a dark carved stone base" },
  { id: "chrome", name: "Modern Chrome", style: "brushed stainless steel sculpture with geometric forms and mirror-polished accents, on a minimal matte black rectangular pedestal" },
  { id: "obsidian", name: "Dark Obsidian", style: "dark obsidian volcanic glass sculpture with purple-green iridescence and ethereal translucency, on a raw black basalt stone base" },
  { id: "walnut", name: "Carved Walnut", style: "hand-carved dark oiled walnut sculpture with visible wood grain and smooth polished curves, on a lighter maple circular display base" },
];

export default function Studio() {
  const [brief, setBrief] = useState("");
  const [contentUrl, setContentUrl] = useState("");
  const [theme, setTheme] = useState(THEMES[0]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [modelUrl, setModelUrl] = useState(null);
  const [modelLoading, setModelLoading] = useState(false);
  const [modelProgress, setModelProgress] = useState(0);
  const modelRef = useRef(null);

  /* ── Simple 3D Model Viewer ── */
  function StudioModelViewer({ url }) {
    const mountRef = useRef(null);
    useEffect(() => {
      if (!url || !mountRef.current) return;
      const w = mountRef.current.clientWidth, h = 400;
      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0x0a0a0a);
      const camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 100);
      camera.position.set(0, 1.5, 3);
      const renderer = new THREE.WebGLRenderer({ antialias: true });
      renderer.setSize(w, h);
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.2;
      mountRef.current.innerHTML = "";
      mountRef.current.appendChild(renderer.domElement);
      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.autoRotate = true;
      controls.autoRotateSpeed = 2;
      const amb = new THREE.AmbientLight(0xffffff, 0.6);
      scene.add(amb);
      const spot = new THREE.DirectionalLight(0xffffff, 2);
      spot.position.set(5, 5, 5);
      scene.add(spot);
      const spot2 = new THREE.DirectionalLight(0xc9a96e, 1);
      spot2.position.set(-3, 3, -3);
      scene.add(spot2);
      new GLTFLoader().load("/api/model-proxy?url=" + encodeURIComponent(url), (gltf) => {
        const model = gltf.scene;
        const box = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        model.position.sub(center);
        model.scale.multiplyScalar(2 / maxDim);
        scene.add(model);
      });
      let animId;
      const animate = () => { animId = requestAnimationFrame(animate); controls.update(); renderer.render(scene, camera); };
      animate();
      return () => { cancelAnimationFrame(animId); renderer.dispose(); };
    }, [url]);
    return <div ref={mountRef} style={{ width: "100%", height: 400, borderRadius: 12, overflow: "hidden" }} />;
  }

  const generate3D = useCallback(async () => {
    if (!result && !brief.trim()) return;
    setModelLoading(true);
    setModelProgress(0);
    setModelUrl(null);
    try {
      const prompt = "Collectible sculpture figurine, museum quality: " + brief.substring(0, 700) + " Single object only, no people, no human figures.";
      const body = result ? { prompt, image_url: result } : { prompt };
      const createRes = await fetch("/api/meshy", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      const createData = await createRes.json();
      if (!createData.result) { setError("3D generation failed to start"); setModelLoading(false); return; }
      const taskId = createData.result;
      for (let i = 0; i < 120; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const pollRes = await fetch("/api/meshy?id=" + taskId);
        const poll = await pollRes.json();
        if (poll.status === "SUCCEEDED" && poll.model_urls?.glb) { setModelUrl(poll.model_urls.glb); setModelProgress(100); setModelLoading(false); return; }
        if (poll.status === "FAILED") { setError("3D generation failed"); setModelLoading(false); return; }
        setModelProgress(poll.progress || Math.min(i * 2, 90));
      }
    } catch (e) { setError("3D error: " + e.message); }
    setModelLoading(false);
  }, [brief, result]);

  const searchImages = useCallback(async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setError(null);
    try {
      const r = await fetch("/api/image-search", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ queries: [searchQuery] }),
      });
      const d = await r.json();
      if (d.results) {
        const imgs = [];
        Object.values(d.results).forEach(arr => arr.forEach(img => imgs.push(img)));
        setSearchResults(imgs);

        /* AI VISION PICK — ask Claude Haiku to select the best reference image */
        if (imgs.length > 0) {
          try {
            const pickRes = await fetch("/api/image-pick", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                images: imgs.slice(0, 10),
                sculpture_description: brief || searchQuery,
              }),
            });
            if (pickRes.ok) {
              const pickData = await pickRes.json();
              if (pickData.picked_image?.url) {
                setContentUrl(pickData.picked_image.url);
                console.log("🎨 AI auto-picked:", pickData.picked_image.title, "—", pickData.reason);
              }
            }
          } catch (e) { console.warn("AI pick failed:", e.message); }
        }
      }
    } catch (e) { setError("Search failed: " + e.message); }
    setSearching(false);
  }, [searchQuery, brief]);

  const generate = useCallback(async () => {
    if (!brief.trim()) return setError("Enter a sculpture brief first");
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const tkRes = await fetch("/api/image");
      const { key } = await tkRes.json();

      /* Build the fusion prompt — clean and short for best FLUX results */
      let prompt;
      if (contentUrl) {
        prompt = `Transform the subject from the reference image into a ${theme.style}. ${brief.substring(0, 300)} No text, no watermarks, no human figures. Dark background, dramatic studio lighting.`;
      } else {
        prompt = `${theme.style}. ${brief.substring(0, 400)} No text, no watermarks, no human figures. Dark background, dramatic studio lighting.`;
      }

      /* Try FLUX.2 Edit if we have content image, otherwise text-only */
      if (contentUrl) {
        const studioRes = await fetch("/api/studio", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ contentImageUrl: contentUrl, prompt, falKey: key }),
        });
        if (studioRes.ok) {
          const data = await studioRes.json();
          if (data.images?.[0]?.url) { setResult(data.images[0].url); setLoading(false); return; }
        } else {
          const errData = await studioRes.json().catch(() => ({}));
          console.warn("FLUX.2 Edit failed, falling back to text-only:", errData.detail);
        }
      }

      /* Fallback: text-only with Flux Pro */
      const res = await fetch("https://fal.run/fal-ai/flux-pro/v1.1", {
        method: "POST",
        headers: { Authorization: "Key " + key, "Content-Type": "application/json" },
        body: JSON.stringify({ prompt, image_size: "landscape_16_9", num_images: 1, safety_tolerance: "2" }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.images?.[0]?.url) { setResult(data.images[0].url); }
        else setError("No image returned");
      } else setError("Generation failed: " + res.status);
    } catch (e) { setError("Error: " + e.message); }
    setLoading(false);
  }, [brief, contentUrl, theme]);

  return (
    <div style={{ minHeight: "100vh", background: "#0a0a0a", color: "#e8e0d4", fontFamily: '"Cormorant Garamond", serif', padding: 0 }}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300&family=Outfit:wght@300;400;500;600&display=swap" rel="stylesheet" />

      {/* Header */}
      <div style={{ padding: "24px 40px", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 300, letterSpacing: "0.05em" }}>THE STUDIO</div>
          <div style={{ fontSize: 11, fontFamily: '"Outfit", sans-serif', color: "rgba(255,255,255,0.4)", marginTop: 4 }}>Yours Truly • Proprietary Sculpture Fusion</div>
        </div>
        <a href="/" style={{ fontSize: 12, fontFamily: '"Outfit", sans-serif', color: "rgba(255,255,255,0.4)", textDecoration: "none" }}>← Back to main</a>
      </div>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "40px 24px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40 }}>

        {/* LEFT — Inputs */}
        <div>
          {/* Step 1: Brief */}
          <div style={{ marginBottom: 32 }}>
            <div style={{ fontSize: 13, fontFamily: '"Outfit", sans-serif', fontWeight: 600, color: "#c9a96e", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.1em" }}>1. CREATIVE BRIEF</div>
            <div style={{ fontSize: 11, fontFamily: '"Outfit", sans-serif', color: "rgba(255,255,255,0.5)", marginBottom: 8 }}>Describe the sculpture — 50% specific memory objects, 50% how it should look as art</div>
            <textarea
              value={brief}
              onChange={e => setBrief(e.target.value)}
              placeholder="Two curved jade dao blades crossed in an X formation, rendered as smooth flowing sculptural forms. The jade is translucent emerald with deep veining..."
              style={{ width: "100%", height: 140, padding: 14, borderRadius: 10, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.04)", color: "#e8e0d4", fontSize: 13, fontFamily: '"Outfit", sans-serif', resize: "vertical", outline: "none" }}
            />
          </div>

          {/* Step 2: Content Image */}
          <div style={{ marginBottom: 32 }}>
            <div style={{ fontSize: 13, fontFamily: '"Outfit", sans-serif', fontWeight: 600, color: "#6baaff", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.1em" }}>2. MEMORY OBJECT IMAGE</div>
            <div style={{ fontSize: 11, fontFamily: '"Outfit", sans-serif', color: "rgba(255,255,255,0.5)", marginBottom: 8 }}>Search for the exact physical object from the memory</div>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === "Enter" && searchImages()}
                placeholder="Kai jade swords Kung Fu Panda 3"
                style={{ flex: 1, padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.04)", color: "#e8e0d4", fontSize: 12, fontFamily: '"Outfit", sans-serif', outline: "none" }}
              />
              <button onClick={searchImages} disabled={searching} style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: "#6baaff", color: "#0a0a0a", fontSize: 12, fontFamily: '"Outfit", sans-serif', fontWeight: 600, cursor: "pointer", opacity: searching ? 0.5 : 1 }}>
                {searching ? "..." : "Search"}
              </button>
            </div>
            {searchResults.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {searchResults.slice(0, 8).map((img, i) => (
                  <img
                    key={i}
                    src={"/api/image-proxy?url=" + encodeURIComponent(img.url || img.thumbnail)}
                    alt={img.title}
                    onClick={() => setContentUrl(img.url || img.thumbnail)}
                    style={{
                      width: 80, height: 80, objectFit: "cover", borderRadius: 8, cursor: "pointer",
                      border: contentUrl === (img.url || img.thumbnail) ? "3px solid #6baaff" : "1px solid rgba(255,255,255,0.1)",
                    }}
                    onError={e => { e.target.style.display = "none"; }}
                  />
                ))}
              </div>
            )}
            {contentUrl && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 10, fontFamily: '"Outfit", sans-serif', color: "#6baaff", marginBottom: 4 }}>Selected:</div>
                <img src={"/api/image-proxy?url=" + encodeURIComponent(contentUrl)} alt="Selected" style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 8, border: "2px solid #6baaff" }} />
                <button onClick={() => setContentUrl("")} style={{ display: "block", marginTop: 4, fontSize: 10, color: "rgba(255,255,255,0.4)", background: "none", border: "none", cursor: "pointer" }}>Clear</button>
              </div>
            )}
          </div>

          {/* Step 3: Theme */}
          <div style={{ marginBottom: 32 }}>
            <div style={{ fontSize: 13, fontFamily: '"Outfit", sans-serif', fontWeight: 600, color: "#aa6bff", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.1em" }}>3. SCULPTURE THEME</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              {THEMES.map(t => (
                <button
                  key={t.id}
                  onClick={() => setTheme(t)}
                  style={{
                    padding: "12px 8px", borderRadius: 10, cursor: "pointer", textAlign: "center",
                    border: theme.id === t.id ? "2px solid #aa6bff" : "1px solid rgba(255,255,255,0.1)",
                    background: theme.id === t.id ? "rgba(170,107,255,0.1)" : "rgba(255,255,255,0.02)",
                    color: theme.id === t.id ? "#aa6bff" : "rgba(255,255,255,0.6)",
                  }}
                >
                  <div style={{ fontSize: 12, fontFamily: '"Outfit", sans-serif', fontWeight: 600 }}>{t.name}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Generate */}
          <button
            onClick={generate}
            disabled={loading}
            style={{
              width: "100%", padding: "16px", borderRadius: 12, border: "none",
              background: loading ? "rgba(201,169,110,0.3)" : "linear-gradient(135deg, #c9a96e 0%, #8B7355 100%)",
              color: "#0a0a0a", fontSize: 16, fontFamily: '"Cormorant Garamond", serif', fontWeight: 600,
              cursor: loading ? "not-allowed" : "pointer", letterSpacing: "0.05em",
            }}
          >
            {loading ? "Creating your sculpture..." : "CREATE SCULPTURE"}
          </button>

          {error && <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: "rgba(255,80,80,0.1)", border: "1px solid rgba(255,80,80,0.3)", fontSize: 12, fontFamily: '"Outfit", sans-serif', color: "#ff6b6b" }}>{error}</div>}
        </div>

        {/* RIGHT — Result */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start", gap: 16 }}>
          {result ? (
            <div style={{ textAlign: "center", width: "100%" }}>
              <img src={result} alt="Generated sculpture" style={{ maxWidth: "100%", borderRadius: 16, boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }} />
              <div style={{ marginTop: 16, fontSize: 11, fontFamily: '"Outfit", sans-serif', color: "rgba(255,255,255,0.4)" }}>
                Generated with {contentUrl ? "FLUX.2 Edit (content + text)" : "Flux Pro (text-only)"} • {theme.name} theme
              </div>

              {/* 3D Model Section */}
              {!modelUrl && !modelLoading && (
                <button onClick={generate3D} style={{
                  marginTop: 16, width: "100%", padding: "14px", borderRadius: 10, border: "1px solid rgba(201,169,110,0.3)",
                  background: "rgba(201,169,110,0.08)", color: "#c9a96e", fontSize: 13, fontFamily: '"Outfit", sans-serif',
                  fontWeight: 500, cursor: "pointer", letterSpacing: "0.03em"
                }}>
                  Generate 3D Model
                </button>
              )}
              {modelLoading && (
                <div style={{ marginTop: 16, padding: "14px", borderRadius: 10, border: "1px solid rgba(201,169,110,0.15)", background: "rgba(201,169,110,0.04)" }}>
                  <div style={{ fontSize: 12, color: "#c9a96e", fontStyle: "italic", marginBottom: 8 }}>Sculpting 3D model... {modelProgress}%</div>
                  <div style={{ width: "100%", height: 3, borderRadius: 2, background: "rgba(201,169,110,0.1)" }}>
                    <div style={{ width: modelProgress + "%", height: "100%", borderRadius: 2, background: "rgba(201,169,110,0.5)", transition: "width 0.5s ease" }} />
                  </div>
                </div>
              )}
              {modelUrl && (
                <div style={{ marginTop: 16, width: "100%" }}>
                  <StudioModelViewer url={modelUrl} />
                  <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: 11, fontFamily: '"Outfit", sans-serif', color: "rgba(255,255,255,0.4)" }}>
                      Drag to rotate • 3D model via Tripo AI
                    </div>
                    <button onClick={async () => {
                      try {
                        const proxyUrl = "/api/model-proxy?url=" + encodeURIComponent(modelUrl);
                        const res = await fetch(proxyUrl);
                        const blob = await res.blob();
                        const a = document.createElement("a");
                        a.href = URL.createObjectURL(blob);
                        a.download = "sculpture_" + Date.now() + ".glb";
                        a.click();
                        URL.revokeObjectURL(a.href);
                      } catch (e) { console.error("Download failed:", e); }
                    }} style={{
                      padding: "6px 16px", borderRadius: 8, border: "1px solid rgba(201,169,110,0.3)",
                      background: "rgba(201,169,110,0.08)", color: "#c9a96e", fontSize: 11,
                      fontFamily: '"Outfit", sans-serif', cursor: "pointer"
                    }}>
                      Download GLB
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : loading ? (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 18, color: "#c9a96e", marginBottom: 8 }}>Creating...</div>
              <div style={{ fontSize: 12, fontFamily: '"Outfit", sans-serif', color: "rgba(255,255,255,0.4)" }}>This may take 15-30 seconds</div>
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: 60 }}>
              <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.2 }}>✦</div>
              <div style={{ fontSize: 22, fontWeight: 300, marginBottom: 8, opacity: 0.4 }}>Your sculpture will appear here</div>
              <div style={{ fontSize: 12, fontFamily: '"Outfit", sans-serif', color: "rgba(255,255,255,0.3)" }}>Write a brief, pick a reference image, choose a theme, and create</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
